import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * FeatureFlagsContext — fetches /api/feature-flags/me on login.
 * Exposes flags map { sales_crm: false, ... }.
 * Safe to use before login (everything is false).
 */
const FeatureFlagsContext = createContext({ flags: {}, loading: true, reload: () => {} });

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export const useFeatureFlag = (key) => {
  const { flags } = useContext(FeatureFlagsContext);
  return !!flags?.[key];
};

export const FeatureFlagsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    // Wait until auth has resolved before deciding flags are empty.
    // Without this guard, a page hard-refresh sets loading=false+flags={}
    // before the auth token is read from localStorage, causing premature
    // redirects in feature-gated pages (e.g. /sales → redirects to /).
    if (authLoading) return;
    if (!user) {
      setFlags({});
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/feature-flags/me');
      setFlags(res.data?.flags || {});
    } catch (e) {
      console.warn('Failed to load feature flags:', e?.message);
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, reload }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
